import { LoginFlow } from "@ory/client-fetch";
import { Login as OryLogin } from "@ory/elements-react/theme";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createSdkErrorHandler, oryConfig, sdk } from "./ory-sdk";

export const Login = () => {
  const [flow, setFlow] = useState<LoginFlow | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const aal2 = searchParams.get("aal2");
  const loginChallenge = searchParams.get("login_challenge");
  const returnTo = searchParams.get("return_to");

  const navigate = useNavigate();

  const getFlow = useCallback(
    async (flowId: string) => {
      const flowData = await sdk.getLoginFlow({ id: flowId });
      setFlow(flowData);
    },
    [setFlow]
  );

  const sdkErrorHandler = createSdkErrorHandler(
    navigate,
    getFlow,
    setFlow,
    "/login",
    true
  );

  const createFlow = async () => {
    try {
      const flowData = await sdk.createBrowserLoginFlow({
        refresh: true,
        aal: aal2 ? "aal2" : "aal1",
        ...(loginChallenge && { loginChallenge: loginChallenge }),
        returnTo: returnTo || window.location.origin,
      });
      const params: Record<string, string> = { flow: flowData.id };
      if (returnTo) params.return_to = returnTo;
      if (loginChallenge) params.login_challenge = loginChallenge;
      if (aal2) params.aal2 = "true";
      setSearchParams(params);
      setFlow(flowData);
    } catch (error) {
      await sdkErrorHandler(error);
    }
  };

  // `createBrowserLoginFlow` mints a new flow and overwrites the CSRF
  // cookie on every call, and Ory Elements renders whichever flow it
  // was first handed without reacting to a later one. React's
  // `StrictMode` runs this effect twice in development, so without a
  // guard two flows are created: the form submits the first flow's
  // token while the cookie holds the second flow's, and Ory rejects
  // the submit as a CSRF violation. Create-or-load exactly once. (The
  // same latent race exists wherever this component is reached without
  // a `?flow=`; a production build, which doesn't double-invoke
  // effects, wouldn't hit it.)
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;

    const flowId = searchParams.get("flow");
    if (flowId) {
      getFlow(flowId).catch(() => createFlow());
      return;
    }

    createFlow();
  }, []);

  if (!flow) {
    return (
      <div className="h-[576px] px-12 pt-12">Loading authorization...</div>
    );
  }

  return (
    <div className="py-0">
      <OryLogin
        flow={flow}
        config={oryConfig}
        components={{
          Card: {
            Footer: () => (
              <div className="flex flex-col gap-2 text-sm mt-4">
                <button
                  type="button"
                  onClick={() => {
                    const search = new URLSearchParams();
                    if (flow.return_to) search.set("return_to", flow.return_to);
                    navigate(
                      { pathname: "/recovery", search: search.toString() },
                      { replace: true }
                    );
                  }}
                  className="text-primary hover:underline"
                >
                  Forgot password?
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const search = new URLSearchParams();
                    if (flow.return_to) search.set("return_to", flow.return_to);
                    if (flow.oauth2_login_challenge)
                      search.set(
                        "login_challenge",
                        flow.oauth2_login_challenge
                      );
                    navigate(
                      { pathname: "/signup", search: search.toString() },
                      { replace: true }
                    );
                  }}
                  className="text-primary hover:underline"
                >
                  Don't have an account? Sign up
                </button>
              </div>
            ),
          },
        }}
      />
    </div>
  );
};
